// src/components/SttHealthIndicator.jsx
//
// Merouani1 §5.2 — "💡 Models up" bulb that sits next to the Connected
// chip in the dashboard header. Four states (grey/amber/green/red) are
// driven by useSttHealth; clicking the bulb opens a small diagnostic
// drawer when the probe is red so the teacher can see the error and
// retry without diving into devtools.

import React, { useState } from 'react';
import BayanIcon from './BayanIcon';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import { LightbulbOutlined, Lightbulb, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useSttHealth, { STT_HEALTH } from '../hooks/useSttHealth';

const colorFor = (state, theme) => {
  switch (state) {
    case STT_HEALTH.GREEN:
      return theme.palette.success.main;
    case STT_HEALTH.AMBER:
      return theme.palette.warning.main;
    case STT_HEALTH.RED:
      return theme.palette.error.main;
    case STT_HEALTH.GREY:
    default:
      return theme.palette.text.disabled;
  }
};

const stateLabel = (state, t) => {
  switch (state) {
    case STT_HEALTH.GREEN:
      return t('stt.modelsUp');
    case STT_HEALTH.AMBER:
      return t('stt.warming');
    case STT_HEALTH.RED:
      return t('stt.modelsDown');
    case STT_HEALTH.GREY:
    default:
      return t('stt.probing');
  }
};

const tooltipFor = (state, detail, error, t) => {
  if (state === STT_HEALTH.GREEN) {
    const latency = detail?.latencyMs;
    const model = detail?.transcribe?.modelKey || 'tarteel';
    return t('stt.tooltipGreen', { latency, model });
  }
  if (state === STT_HEALTH.AMBER) {
    return t('stt.tooltipAmber');
  }
  if (state === STT_HEALTH.RED) {
    return t('stt.tooltipRed', { error: error || '' });
  }
  return t('stt.tooltipGrey');
};

const SttHealthIndicator = ({ sx, options }) => {
  const { t } = useTranslation();
  const { state, detail, error, refresh } = useSttHealth(options);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isRed = state === STT_HEALTH.RED;
  const icon =
    state === STT_HEALTH.GREEN ? (
      <Lightbulb fontSize="small" />
    ) : (
      <LightbulbOutlined fontSize="small" />
    );

  const handleClick = () => {
    if (isRed) setDrawerOpen(true);
    else refresh();
  };

  return (
    <>
      <Tooltip title={tooltipFor(state, detail, error, t)} arrow>
        <Chip
          size="small"
          icon={icon}
          label={stateLabel(state, t)}
          variant="outlined"
          onClick={handleClick}
          role="status"
          aria-live="polite"
          aria-label={`${t('stt.indicatorAriaLabel')}: ${stateLabel(state, t)}`}
          sx={(theme) => ({
            display: { xs: 'none', sm: 'inline-flex' },
            borderColor: colorFor(state, theme),
            color: colorFor(state, theme),
            fontFamily: 'var(--sacred-font-label)',
            fontSize: '0.7rem',
            cursor: 'pointer',
            '& .MuiChip-icon': { color: 'inherit' },
            ...(sx || {}),
          })}
          data-testid="stt-health-indicator"
          data-state={state}
        />
      </Tooltip>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 2 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {t('stt.diagnosticTitle')}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="close">
            <Close />
          </IconButton>
        </Stack>

        <Alert severity={isRed ? 'error' : 'info'} sx={{ mb: 2 }}>
          {isRed ? error || t('stt.tooltipRed', { error: '' }) : stateLabel(state, t)}
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('stt.diagnosticHint')}
        </Typography>

        <Stack spacing={1}>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary">
              REACT_APP_LOCAL_STT_URL
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {process.env.REACT_APP_LOCAL_STT_URL || t('stt.envUnset')}
            </Typography>
          </Box>
          <Divider />
          <Button
            startIcon={<BayanIcon name="repeat_loop" />}
            variant="outlined"
            onClick={() => {
              refresh();
              setDrawerOpen(false);
            }}
          >
            {t('stt.retry')}
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};

export default SttHealthIndicator;
